"use client";

import React, { useState, useEffect, useMemo } from "react";
import { Phone, Facebook, X } from "lucide-react";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";
import { cn } from "@/utils/cn";
import { motion, AnimatePresence } from "framer-motion";
import ActionButton from "@/components/Buttons/ActionButton";
import { useMutation } from "@tanstack/react-query";
import { postContact } from "@/apis/queryFunctions/contact";
import { useToast } from "@/context/ToastProvider";
import { MontserratText } from "@/components/Typography";

interface FloatingActionsProps {
  onClickRegister?: () => void;
  phone?: string;
  zalo?: string;
  facebook?: string;
  consult?: string;
  contact?: any;
}

export const FloatingActions = ({
  onClickRegister,
  phone,
  zalo,
  facebook,
  consult,
  contact,
}: FloatingActionsProps) => {
  const displayPhone = phone;
  const displayZalo = zalo;
  const displayFacebook = facebook || "https://facebook.com";

  const [isVisible, setIsVisible] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    message: "",
  });

  const defaultContact = {
    btnSubmit: "Gửi Đăng Ký",
    btnSending: "Đang gửi...",
    errorSubmit: "Gửi thông tin không thành công",
    errorSystem: "Có lỗi xảy ra, vui lòng thử lại",
    successTitle: "Đăng Ký Thành Công!",
    successDesc:
      "Cảm ơn quý khách đã quan tâm. Chúng tôi sẽ liên hệ lại trong thời gian sớm nhất.",
    btnClose: "Đóng",
    ...contact,
  };

  const contactMutation = useMutation({
    mutationFn: postContact,
    onSuccess: (rs: any) => {
      if (rs && Object.keys(rs).length > 0) {
        setIsModalOpen(false);
        setShowSuccessModal(true);
        setFormData({ name: "", phone: "", message: "" });
      } else {
        showToast(defaultContact.errorSubmit, "error");
      }
    },
    onError: () => {
      showToast(defaultContact.errorSystem, "error");
    },
  });

  const [formErrors, setFormErrors] = useState({
    name: "",
    phone: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = formData.name.trim();
    const trimmedPhone = formData.phone.trim();
    const trimmedMessage = formData.message.trim();

    let hasError = false;
    const errors = { name: "", phone: "" };

    if (!trimmedName) {
      errors.name = "Vui lòng nhập họ tên của quý khách";
      hasError = true;
    }

    if (!trimmedPhone) {
      errors.phone = "Vui lòng nhập số điện thoại";
      hasError = true;
    } else {
      const phoneRegex = /^(0|\+84)[0-9]{9}$/;
      if (!phoneRegex.test(trimmedPhone)) {
        errors.phone = "Số điện thoại không hợp lệ";
        hasError = true;
      }
    }

    if (hasError) {
      setFormErrors(errors);
      return;
    }

    contactMutation.mutate({
      ho_ten: trimmedName,
      dien_thoai: trimmedPhone,
      noi_dung: trimmedMessage,
    });
  };

  useEffect(() => {
    const footer = document.getElementById("main-footer");
    if (!footer) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(!entry.isIntersecting);
      },
      {
        threshold: 0.05,
      },
    );

    observer.observe(footer);
    return () => {
      observer.unobserve(footer);
    };
  }, []);

  useEffect(() => {
    if (isModalOpen || showSuccessModal) {
      document.documentElement.style.overflow = "hidden";
      document.documentElement.style.height = "100%";
      document.body.style.overflow = "hidden";
      document.body.style.height = "100%";
    } else {
      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";
      document.body.style.overflow = "";
      document.body.style.height = "";
    }
    return () => {
      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, [isModalOpen, showSuccessModal]);

  const handleRegisterClick = () => {
    if (onClickRegister) {
      onClickRegister();
    } else {
      setIsModalOpen(true);
    }
  };

  const renderedStyles = useMemo(
    () => (
      <style>{`
      @keyframes ripple-ring-expand {
        0% {
          transform: scale(1);
          opacity: 1;
          border-color: rgba(219, 182, 83, 0.95);
          box-shadow: 0 0 4px rgba(219, 182, 83, 0.6);
        }
        100% {
          transform: scale(2.2);
          opacity: 0;
          border-color: rgba(219, 182, 83, 0);
          box-shadow: 0 0 20px rgba(219, 182, 83, 0);
        }
      }
      .auto-nhay-ring {
        position: absolute;
        inset: 0;
        pointer-events: none;
        z-index: 0;
      }
      .auto-nhay-ring div {
        position: absolute;
        inset: 0;
        border: 2px solid rgba(219, 182, 83, 0.9);
        border-radius: inherit;
        animation: ripple-ring-expand 2.0s infinite cubic-bezier(0.25, 0, 0, 1);
      }
      .auto-nhay-ring div:nth-child(1) {
        animation-delay: 0s;
      }
      .auto-nhay-ring div:nth-child(2) {
        animation-delay: 0.5s;
      }
      .auto-nhay-ring div:nth-child(3) {
        animation-delay: 1.0s;
      }
      .auto-nhay-ring div:nth-child(4) {
        animation-delay: 1.5s;
      }
      
      @keyframes auto-text-gold-blink {
        0%, 100% {
          color: #ffffff;
          text-shadow: 0 0 0px transparent;
          transform: rotate(180deg) scale(1);
        }
        50% {
          color: #DBB653;
          text-shadow: 0 0 10px rgba(219, 182, 83, 0.85);
          transform: rotate(180deg) scale(1.08);
        }
      }
      .auto-nhay-text {
        animation: auto-text-gold-blink 1.8s infinite ease-in-out;
      }
      
      @keyframes auto-logo-gold-blink {
        0%, 100% {
          filter: brightness(0) invert(1) drop-shadow(0 0 0px transparent);
          transform: scale(1);
        }
        50% {
          filter: brightness(0) invert(1) drop-shadow(0 0 6px rgba(219, 182, 83, 0.95));
          transform: scale(1.3);
        }
      }
      .auto-nhay-logo {
        animation: auto-logo-gold-blink 2.2s infinite ease-in-out;
        transform-origin: center;
      }
      .group:hover .auto-nhay-logo {
        animation: auto-logo-gold-blink 0.8s infinite ease-in-out;
      }

      @keyframes container-gold-glow {
        0%, 100% {
          border-color: rgba(219, 182, 83, 0.45);
          box-shadow: 0 12px 35px -10px rgba(0, 0, 0, 0.6), 0 0 15px rgba(219, 182, 83, 0.15);
        }
        50% {
          border-color: rgba(219, 182, 83, 0.85);
          box-shadow: 0 12px 40px -10px rgba(0, 0, 0, 0.7), 0 0 25px rgba(219, 182, 83, 0.45);
        }
      }
      .glow-gold-container {
        animation: container-gold-glow 3s infinite ease-in-out;
      }

      @keyframes shine-sweep-vertical {
        0% {
          top: -150%;
        }
        20% {
          top: 150%;
        }
        100% {
          top: 150%;
        }
      }
      .shine-sweep-button::after {
        content: "";
        position: absolute;
        left: 0;
        top: -150%;
        width: 100%;
        height: 80px;
        background: linear-gradient(
          180deg,
          transparent,
          rgba(219, 182, 83, 0.65),
          transparent
        );
        animation: shine-sweep-vertical 2.5s infinite ease-in-out;
        pointer-events: none;
        z-index: 5;
      }

      /* Micro-interactions */
      @keyframes auto-icon-tilt-sway {
        0%, 100% { transform: rotate(-20deg); }
        50% { transform: rotate(20deg); }
      }
      .auto-nhay-icon {
        animation: auto-icon-tilt-sway 2s infinite ease-in-out;
        transform-origin: center;
        display: inline-block;
      }
      .group\/btn:hover .auto-nhay-icon {
        animation: auto-icon-tilt-sway 0.6s infinite ease-in-out;
        transform: scale(1.15);
      }
    `}</style>
    ),
    [],
  );

  return (
    <>
      {renderedStyles}

      <div
        className={cn(
          "fixed bottom-14 right-4 md:right-0 md:top-1/2 md:-translate-y-1/2 md:bottom-auto z-50 flex flex-col gap-3 items-end transition-all duration-300",
          isVisible
            ? "opacity-100 scale-100 pointer-events-auto"
            : "opacity-0 scale-95 pointer-events-none",
        )}
      >
        {/* Social Contacts Pill */}
        <div className="w-10 md:w-[52px] bg-[#0d3f4d]/90 border md:border-r-0 md:border-l md:border-y border-white/10 rounded-full md:rounded-r-none md:rounded-l-2xl py-2.5 md:py-3 px-0 flex flex-col gap-2.5 md:gap-3.5 items-center shadow-2xl backdrop-blur-md glow-gold-container">
          {/* Phone */}
          <a
            href={`tel:${displayPhone}`}
            className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-white flex items-center justify-center text-[#0d3f4d] shadow-md hover:scale-125 hover:-translate-x-1 hover:shadow-[0_0_20px_rgba(219,182,83,0.85)] transition-all duration-200 relative group/btn"
            title="Call Us"
          >
            <div className="auto-nhay-ring rounded-full">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
            <Phone className="w-3.5 h-3.5 md:w-4 md:h-4 fill-[#0d3f4d] relative z-10 auto-nhay-icon" />

            {/* Custom Tooltip */}
            <div className="absolute right-full mr-3.5 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-lg bg-[#0d3f4d]/95 border border-[#DBB653]/50 text-white text-xs font-semibold whitespace-nowrap opacity-0 translate-x-2 pointer-events-none transition-all duration-300 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 shadow-xl backdrop-blur-md">
              Hotline: {formatPhoneNumber(displayPhone || "0937 657 968")}
            </div>
          </a>

          {/* Zalo */}
          <a
            href={`https://zalo.me/${displayZalo}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-white flex items-center justify-center shadow-md hover:scale-125 hover:-translate-x-1 hover:shadow-[0_0_20px_rgba(219,182,83,0.85)] transition-all duration-200 relative group/btn"
            title="Zalo Chat"
          >
            <div className="auto-nhay-ring rounded-lg">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
            <img
              src="/icons/zalo_icon_white.svg"
              alt="Zalo"
              className="w-full h-full relative z-10 auto-nhay-icon rounded-lg object-cover"
            />

            {/* Custom Tooltip */}
            <div className="absolute right-full mr-3.5 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-lg bg-[#0d3f4d]/95 border border-[#DBB653]/50 text-white text-xs font-semibold whitespace-nowrap opacity-0 translate-x-2 pointer-events-none transition-all duration-300 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 shadow-xl backdrop-blur-md">
              Chat Zalo
            </div>
          </a>

          {/* Facebook */}
          <a
            href={displayFacebook}
            target="_blank"
            rel="noopener noreferrer"
            className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-white flex items-center justify-center text-[#0d3f4d] shadow-md hover:scale-125 hover:-translate-x-1 hover:shadow-[0_0_20px_rgba(219,182,83,0.85)] transition-all duration-200 relative group/btn"
            title="Facebook FanPage"
          >
            <div className="auto-nhay-ring rounded-full">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
            <Facebook className="w-3.5 h-3.5 md:w-4 md:h-4 fill-[#0d3f4d] text-[#0d3f4d] relative z-10 auto-nhay-icon" />

            {/* Custom Tooltip */}
            <div className="absolute right-full mr-3.5 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-lg bg-[#0d3f4d]/95 border border-[#DBB653]/50 text-white text-xs font-semibold whitespace-nowrap opacity-0 translate-x-2 pointer-events-none transition-all duration-300 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 shadow-xl backdrop-blur-md">
              Fanpage
            </div>
          </a>
        </div>

        {/* Register Consultancy Pill */}
        <div
          onClick={handleRegisterClick}
          className="hidden md:flex w-[52px] group bg-[#0d3f4d]/90 border-l border-y rounded-l-2xl py-5 px-0 flex-col gap-4 items-center shadow-2xl backdrop-blur-md cursor-pointer transition-all duration-300 hover:bg-[#124d5e] hover:-translate-x-1.5 relative overflow-hidden glow-gold-container shine-sweep-button"
        >
          <span
            style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
            className="text-[11px] md:text-xs font-bold tracking-widest text-white uppercase select-none transform rotate-180 duration-300 group-hover:text-[#DBB653] transition-colors relative z-10 auto-nhay-text"
          >
            {consult}
          </span>
          {/* <div className="transition-transform duration-300 relative z-10">
            <img
              src="/logos/ZenGroup_Logo.svg"
              alt="ZenGroup Logo"
              className="w-5 h-5 object-contain brightness-0 invert transition-all duration-300 auto-nhay-logo"
            />
          </div> */}
        </div>
      </div>

      {/* Modal Đăng Ký Tư Vấn */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
            {/* Modal backdrop closer */}
            <div
              className="absolute inset-0"
              onClick={() => setIsModalOpen(false)}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-md bg-[#051a21] border border-[#e5c179]/20 p-6 md:p-8 rounded-2xl shadow-[0_20px_50px_-15px_rgba(0,0,0,0.5)] relative text-white z-10"
            >
              {/* Close button */}
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors focus:outline-none"
              >
                <X size={20} />
              </button>

              {/* Decorative top bar */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1/3 h-[3px] bg-[#e5c179]" />

              <div className="text-center mb-6 mt-2">
                <p className="text-[#e5c179] text-[10px] font-bold uppercase tracking-[0.2em] mb-1.5">
                  Thông tin đặc quyền
                </p>
                <h4 className="text-lg md:text-xl font-bold text-white uppercase tracking-wide">
                  ĐĂNG KÝ NHẬN THÔNG TIN
                </h4>
                <p className="text-gray-400 text-xs mt-1 font-light italic">
                  Thông tin bảo mật và được xử lý trong vòng 5 phút
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Họ tên */}
                {/* Họ tên */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-gray-300 uppercase tracking-wider">
                    Họ và tên <span className="text-red-400">(*)</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Vui lòng nhập họ và tên"
                    className="w-full bg-[#0a2530]/60 text-white px-4 py-2.5 rounded border border-white/10 outline-none text-xs focus:ring-1 focus:ring-[#e5c179] focus:border-[#e5c179] placeholder-white/20 transition-all duration-300"
                  />
                  {formErrors.name && (
                    <MontserratText
                      as="p"
                      mobile="10"
                      className="text-yellow-300 mt-1"
                    >
                      {formErrors.name}
                    </MontserratText>
                  )}
                </div>

                {/* Số điện thoại */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-gray-300 uppercase tracking-wider">
                    Số điện thoại <span className="text-red-400">(*)</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Vui lòng nhập số điện thoại"
                    className="w-full bg-[#0a2530]/60 text-white px-4 py-2.5 rounded border border-white/10 outline-none text-xs focus:ring-1 focus:ring-[#e5c179] focus:border-[#e5c179] placeholder-white/20 transition-all duration-300"
                  />
                  {formErrors.phone && (
                    <MontserratText
                      as="p"
                      mobile="10"
                      className="text-yellow-300 mt-1"
                    >
                      {formErrors.phone}
                    </MontserratText>
                  )}
                </div>

                {/* Nội dung tư vấn */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-gray-300 uppercase tracking-wider">
                    Nội dung tư vấn{" "}
                    <span className="text-gray-400 font-light">
                      (Không bắt buộc)
                    </span>
                  </label>
                  <textarea
                    name="message"
                    rows={3}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Vui lòng nhập nội dung tư vấn"
                    className="w-full bg-[#0a2530]/60 text-white px-4 py-2.5 rounded border border-white/10 outline-none text-xs focus:ring-1 focus:ring-[#e5c179] focus:border-[#e5c179] placeholder-white/20 transition-all duration-300 resize-none"
                  />
                </div>

                {/* Submit button */}
                <div className="pt-2">
                  <ActionButton
                    type="submit"
                    label={
                      contactMutation.isPending
                        ? defaultContact.btnSending
                        : defaultContact.btnSubmit
                    }
                    className={`w-full h-[40px] !py-0 !px-0 flex items-center justify-center text-xs font-bold ${
                      contactMutation.isPending
                        ? "opacity-60 pointer-events-none"
                        : ""
                    }`}
                    showIcon={false}
                  />
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Success Popup Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-[999999] flex items-center justify-center transition-all duration-300">
          <div className="bg-[#0d3f4d] border border-[#DBB653] rounded-[20px] p-8 max-w-sm w-full mx-4 flex flex-col items-center text-center shadow-[0_20px_50px_rgba(0,0,0,0.6)] relative overflow-hidden text-white">
            {/* Background gold pulse */}
            <div className="absolute -top-12 -right-12 w-24 h-24 bg-[#DBB653]/10 rounded-full blur-xl" />
            <div className="absolute -bottom-12 -left-12 w-24 h-24 bg-[#DBB653]/10 rounded-full blur-xl" />

            {/* Checkmark Icon */}
            <div className="w-16 h-16 bg-[#DBB653]/10 border border-[#DBB653]/30 rounded-full flex items-center justify-center mb-6">
              <svg
                className="w-8 h-8 text-[#DBB653]"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 12.75l6 6 9-13.5"
                ></path>
              </svg>
            </div>

            {/* Content */}
            <h3 className="text-white font-montserrat font-bold text-[20px] uppercase tracking-wide mb-3">
              {defaultContact.successTitle}
            </h3>
            <p className="text-white/70 text-[13px] font-montserrat leading-relaxed mb-8">
              {defaultContact.successDesc}
            </p>

            {/* Close Button */}
            <ActionButton
              type="button"
              onClick={() => setShowSuccessModal(false)}
              label={defaultContact.btnClose}
            />
          </div>
        </div>
      )}
    </>
  );
};
